import { useCallback, useRef, useState } from 'react';
import api from '../utils/axios';
import type { TagChip } from '../components/ChatInput/ChatInput.types';

interface UseTagSearchOptions {
  /**
   * Suggest endpoint path (e.g. `/cab/${projectId}/tags/suggest`).
   * Pass `null` to disable searching (e.g. while projectId is unknown).
   */
  endpoint: string | null;
  /** Auth token; sent as a Bearer Authorization header when present. */
  token?: string | null;
  /** Minimum query length before hitting the endpoint. Defaults to 2. */
  minQueryLength?: number;
  /** Max suggestions requested from the backend. Defaults to 20. */
  limit?: number;
  /** Maps the endpoint's response payload to TagChip suggestions. */
  mapResponse: (data: any) => TagChip[];
}

/**
 * Shared @-tag autocomplete search: query handler with
 * AbortController cancellation of in-flight requests. Used by CAB chat
 * (CABChatInput) and Build-mode ticket/epic references (ChatPage).
 */
export function useTagSearch({
  endpoint,
  token,
  minQueryLength = 2,
  limit = 20,
  mapResponse,
}: UseTagSearchOptions) {
  const [suggestions, setSuggestions] = useState<TagChip[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleTagSearch = useCallback(async (query: string) => {
    if (!query.trim() || query.length < minQueryLength || !endpoint) {
      setSuggestions([]);
      setIsSearching(false);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsSearching(true);
    try {
      const response = await api.get(endpoint, {
        params: { q: query, limit },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        signal: controller.signal,
      });
      setSuggestions(mapResponse(response.data));
    } catch (error: any) {
      if (error?.name !== 'CanceledError') {
        console.error('Tag search failed:', error);
      }
    } finally {
      if (!controller.signal.aborted) {
        setIsSearching(false);
      }
    }
  }, [endpoint, token, minQueryLength, limit, mapResponse]);

  return { suggestions, isSearching, handleTagSearch };
}
